import { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { FLEXIBLE_NOTE_PII_FIELDS } from '@lyrahealth-inc/shared-app-logic/src/features/notes/flexibleNotePII'
import { attendanceValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { FormMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { applySpecialtyCareAttestation } from '../../../common/components/healthPlanInfo/getSpecialtyCareAttestationExtension'
import { getPaymentsDataAllData } from '../../../data/payments/paymentsSelectors'
import { useCustomersForClient } from '../../clients/clientDetails/data/hooks/useCustomersForClient'
import { useSelectedClientData } from '../../clients/clientDetails/data/hooks/useSelectedClientData'
import { CloseSessionEligibilityResult } from '../../sessions/endOfSession/hooks/useCloseSessionEligibility'
import { useCloseSessionMetadata } from '../../sessions/endOfSession/hooks/useCloseSessionMetadata'
import { FLEX_NOTE_CLOSE_SESSION_READONLY_FIELDS, FLEX_NOTE_EPISODE_CLOSURE_FIELDS } from '../utils/noteConstants'
import { getCloseSessionFieldsReadOnlyMetadata } from '../utils/reversedChargeNoteUtils'

type UseFlexibleNoteMetadataParams = {
  baseMetadata: any
  isFlexibleNote: boolean
  isEAPSession: boolean
  appointmentToClose: Appointment | undefined
  selectedEpisode: Episode | undefined
  eligibility: CloseSessionEligibilityResult
  chargeAlreadySubmitted: boolean
  noteIsCompleted: boolean
  /** Saved note body values. Used on completed notes to anchor episode-closure field visibility to
   *  what was actually answered at submission time, rather than the current dynamic canEndEpisode state. */
  noteBody?: Record<string, any>
}

// The standalone end-of-session form labels the condition fields with clinical-condition wording;
// the flexible note relabels them as "treatment focus" for EAP sessions.
const EAP_PRIMARY_CONDITION_TITLE = 'Primary focus for treatment'
const EAP_SECONDARY_CONDITION_TITLE = 'Secondary focus areas for treatment (Select all that apply)'

const SESSION_RATE_FIELDS = new Set(['sessionRateMessage', 'sessionRateMessageInEligible'])

/**
 * Composes the consolidated DA Flexible Note metadata.
 *
 * The note JSON owns the layout (`ui:order`) and the note/flex-specific fields. The shared
 * close-session fields (modality, reference_number, the session-rate messages, the treatment-focus
 * header/description, and primary/secondary condition + their "other" descriptions) are sourced from
 * `useCloseSessionMetadata` — the same metadata the standalone end-of-session screen uses, with the
 * modality options, session rate, and conditions list already baked in.
 *
 * Mechanism: any field name listed in the note's `ui:order` that the note doesn't define itself is
 * grafted from the close-session metadata. Primary/secondary condition titles are relabeled for EAP.
 *
 * Returns the base metadata unchanged when it isn't a flexible note (or there's no appointment to
 * close, so the close-session metadata can't be built).
 */
export const useFlexibleNoteMetadata = ({
  baseMetadata,
  isFlexibleNote,
  isEAPSession,
  appointmentToClose,
  selectedEpisode,
  eligibility,
  chargeAlreadySubmitted,
  noteIsCompleted,
  noteBody,
}: UseFlexibleNoteMetadataParams): any => {
  const { data: clientDetails } = useSelectedClientData()
  const customers = useCustomersForClient()
  const pastPayments = useSelector(getPaymentsDataAllData)
  const { label: companyLabel } = customers?.find(({ value }: any) => value === clientDetails?.employer) || {}

  const { metadata: closeSessionMetadata } = useCloseSessionMetadata({
    appointmentToClose,
    selectedEpisode,
    isProviderEngageDA: true,
    isProviderCoach: false,
    useEnhancedDiagnosisCapture: true,
    shouldShowGuardianConsent: eligibility.shouldShowGuardianConsent,
    nextSessionUsesHealthPlan: Boolean(eligibility.eligibilityObj?.next_session_uses_health_plan),
    calculatedSessionRate: eligibility.sessionDetails?.sessionRate ?? 0,
    isDaFlexibleNote: true,
  })

  const noteName: string | undefined = baseMetadata?.name
  const rawNoteFieldKeys = useRef<{ noteName?: string; keys: Set<string> }>({ keys: new Set() })
  if (rawNoteFieldKeys.current.noteName !== noteName) {
    const keys = new Set(Object.keys(baseMetadata?.meta_data?.schema?.properties ?? {}))
    FLEXIBLE_NOTE_PII_FIELDS.forEach((field) => keys.delete(field))
    rawNoteFieldKeys.current = {
      noteName,
      keys,
    }
  }

  return useMemo(() => {
    const noteMetaData = baseMetadata?.meta_data
    const order: string[] | undefined = noteMetaData?.uiSchema?.['ui:order']
    const noteProperties = noteMetaData?.schema?.properties

    if (!isFlexibleNote || !appointmentToClose || !order || !noteProperties) {
      return baseMetadata
    }

    const rawNoteKeys = rawNoteFieldKeys.current.keys
    const eosProperties = (closeSessionMetadata?.schema?.properties ?? {}) as Record<string, any>
    const eosUiSchema = (closeSessionMetadata?.uiSchema ?? {}) as Record<string, any>
    const eosInitialValues = (closeSessionMetadata?.initialValues ?? {}) as Record<string, any>

    const graftedProperties: Record<string, any> = {}
    const graftedUiSchema: Record<string, any> = {}
    const graftedInitialValues: Record<string, any> = {}

    // Graft every ui:order field the note doesn't define itself from the close-session metadata. We
    // check the note's RAW field set (not the current properties), so close-session fields we grafted
    // on a previous render — e.g. `sessionRateMessage`, whose `sessionRate` recomputes with the chosen
    // modality — get overwritten with the latest value rather than treated as already present. We also
    // carry over each grafted field's initial value (e.g. `modality`, defaulted from the appointment's
    // meeting format) so the field is pre-selected — without this the modality radio starts blank and
    // the session rate is computed with no modality (base rate) until the provider re-picks it.
    order.forEach((fieldName) => {
      if (noteIsCompleted && SESSION_RATE_FIELDS.has(fieldName)) return
      if (chargeAlreadySubmitted && FLEX_NOTE_EPISODE_CLOSURE_FIELDS.has(fieldName)) return
      if (noteIsCompleted && FLEX_NOTE_EPISODE_CLOSURE_FIELDS.has(fieldName) && !(fieldName in (noteBody ?? {}))) return
      if (!rawNoteKeys.has(fieldName) && eosProperties[fieldName]) {
        graftedProperties[fieldName] = eosProperties[fieldName]
        if (eosUiSchema[fieldName]) {
          graftedUiSchema[fieldName] = eosUiSchema[fieldName]
        }
        if (eosInitialValues[fieldName] !== undefined) {
          graftedInitialValues[fieldName] = eosInitialValues[fieldName]
        }
      }
    })

    // On the flexible note a clientIneligible session still happened, so the grafted clinical/close-session
    // fields (gated attended-only on EOS) should show for clientIneligible too — only a no-show hides them.
    // The normal session-rate message stays attended-only; the ineligible-rate message replaces it.
    Object.keys(graftedProperties).forEach((fieldName) => {
      if (fieldName === 'sessionRateMessage') return
      const condition = graftedProperties[fieldName]?.condition
      if (condition?.parentField === 'attendance' && condition?.parentValue === attendanceValues.ATTENDED) {
        graftedProperties[fieldName] = {
          ...graftedProperties[fieldName],
          condition: {
            parentField: 'attendance',
            parentValue: [attendanceValues.ATTENDED, attendanceValues.INELIGIBLE],
          },
        }
      }
    })

    if (isEAPSession) {
      if (graftedProperties.primary_condition) {
        graftedProperties.primary_condition = {
          ...graftedProperties.primary_condition,
          title: EAP_PRIMARY_CONDITION_TITLE,
        }
      }
      if (graftedProperties.secondary_condition) {
        graftedProperties.secondary_condition = {
          ...graftedProperties.secondary_condition,
          title: EAP_SECONDARY_CONDITION_TITLE,
        }
      }
    }

    if (graftedProperties.secondary_condition) {
      const { placeholder: _, ...rest } = graftedProperties.secondary_condition
      graftedProperties.secondary_condition = rest
    }

    // Strip session-rate fields from base properties so they can't enter via noteProperties or graftedProperties.
    const filteredProperties = Object.fromEntries(
      Object.entries(noteProperties).filter(([key]) => {
        if (noteIsCompleted && SESSION_RATE_FIELDS.has(key)) return false
        if (chargeAlreadySubmitted && FLEX_NOTE_EPISODE_CLOSURE_FIELDS.has(key)) return false
        if (noteIsCompleted && FLEX_NOTE_EPISODE_CLOSURE_FIELDS.has(key) && !(key in (noteBody ?? {}))) return false
        return true
      }),
    )

    let consolidatedFormMetadata: FormMetadata = {
      schema: {
        ...noteMetaData.schema,
        properties: { ...filteredProperties, ...graftedProperties },
      },
      uiSchema: { ...noteMetaData.uiSchema, ...graftedUiSchema },
      initialValues: {
        ...noteMetaData.initialValues,
        ...graftedInitialValues,
      },
    }

    consolidatedFormMetadata = applySpecialtyCareAttestation(consolidatedFormMetadata, {
      specialtyCareAttestationStatus: eligibility.eligibilityObj?.speciality_care_attestation_status as
        | string
        | undefined,
      companyLabel,
      firstName: clientDetails?.first_name,
      fullName: clientDetails?.full_name,
      pastHealthPlanPayments: pastPayments?.filter((pastPayment: any) => pastPayment.is_health_plan),
    })

    const consolidatedMetadata = {
      ...baseMetadata,
      meta_data: {
        ...noteMetaData,
        ...consolidatedFormMetadata,
      },
    }

    if (chargeAlreadySubmitted) {
      return getCloseSessionFieldsReadOnlyMetadata(consolidatedMetadata, FLEX_NOTE_CLOSE_SESSION_READONLY_FIELDS)
    }

    return consolidatedMetadata
  }, [
    baseMetadata,
    isFlexibleNote,
    isEAPSession,
    appointmentToClose,
    closeSessionMetadata,
    chargeAlreadySubmitted,
    noteIsCompleted,
    noteBody,
    companyLabel,
    clientDetails,
    pastPayments,
    eligibility.eligibilityObj?.speciality_care_attestation_status,
  ])
}
